/**
 * Commitment Form Widget - Embeddable Bundle Entry Point
 *
 * Generic pledge commitment widget. Embed it on any page, point it at a
 * `Pledge_Campaign_ID`, and it will:
 *   - require an MP user login
 *   - show identity cards (submitter + spouse if detected)
 *   - capture annual giving + expanded giving + (optionally) stored resources
 *   - write to Pledge_Campaign_Commitments
 *
 * Same React tree as the Next.js dev app, bundled into a single IIFE.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { CommitmentForm } from '../src/components/commitment-form/CommitmentForm';
import { WidgetConfigProvider } from '../src/lib/widget-config';
import { PortalContainerProvider } from '@church/nextjs-ui/bottom-sheet';
import styleText from '../src/app/globals.css?inline';

interface WidgetConfig {
  apiBaseUrl?: string;
  containerId?: string;
  /** Required. The Pledge_Campaigns row to write commitments against.
   *  Can also be supplied as `data-campaign-id="..."` on the container div,
   *  which keeps the host-page embed to a clean two lines. */
  campaignId: number;
  /** Reserved for future per-campaign theming. Today only 'see-jesus' is defined. */
  theme?: string;
}

/** Where the widget's static assets (gold foil bg, logo) are served from.
 *  Vite bakes this in at build time; locally we fall back to '' so the dev
 *  preview's own /assets paths keep working. */
const ASSET_BASE_URL = (process.env.CHURCH_HUB_WIDGET_ASSET_BASE_URL || '').replace(/\/$/, '');

declare global {
  interface Window {
    COMMITMENT_FORM_WIDGET_CONFIG?: WidgetConfig;
    CommitmentFormWidget?: CommitmentFormWidgetInstance;
  }
}

class CommitmentFormWidgetInstance {
  private root: ReturnType<typeof createRoot> | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private config: WidgetConfig;

  constructor(config: WidgetConfig) {
    this.config = {
      apiBaseUrl: config.apiBaseUrl || window.location.origin,
      containerId: config.containerId || 'commitment-form-root',
      theme: config.theme || 'see-jesus',
      ...config,
    };
  }

  init() {
    if (!this.config.campaignId) {
      console.error(
        'Commitment Form Widget: window.COMMITMENT_FORM_WIDGET_CONFIG.campaignId is required'
      );
      return;
    }

    const container = document.getElementById(this.config.containerId!);
    if (!container) {
      console.error(`Commitment Form Widget: Container #${this.config.containerId} not found`);
      return;
    }

    if (container.shadowRoot) {
      this.shadowRoot = container.shadowRoot;
      const existingApp = this.shadowRoot.getElementById('commitment-form-widget-app');
      if (existingApp) {
        console.log('Commitment Form Widget: already initialized, skipping');
        return;
      }
    } else {
      this.shadowRoot = container.attachShadow({ mode: 'open' });
    }

    const mountPoint = document.createElement('div');
    mountPoint.id = 'commitment-form-widget-app';
    this.shadowRoot.appendChild(mountPoint);

    const portalContainer = document.createElement('div');
    portalContainer.id = 'commitment-form-widget-portals';
    this.shadowRoot.appendChild(portalContainer);

    this.injectFonts();
    this.injectStyles();
    this.preventScrollLock();

    this.root = createRoot(mountPoint);
    this.root.render(
      <React.StrictMode>
        <PortalContainerProvider container={portalContainer}>
          <WidgetConfigProvider
            value={{
              apiBaseUrl: this.config.apiBaseUrl!,
              campaignId: this.config.campaignId,
              theme: this.config.theme!,
            }}
          >
            <CommitmentForm />
          </WidgetConfigProvider>
        </PortalContainerProvider>
      </React.StrictMode>
    );

    console.log('Commitment Form Widget initialized', this.config);
  }

  private preventScrollLock() {
    if ((window as any).__COMMITMENT_FORM_WIDGET_SCROLL_LOCK_PREVENTED__) return;

    const cleanElement = (el: HTMLElement) => {
      if (el.hasAttribute('data-scroll-locked')) el.removeAttribute('data-scroll-locked');
      if (el.style.getPropertyValue('overflow') === 'hidden') el.style.removeProperty('overflow');
      if (el.style.getPropertyValue('padding-right')) el.style.removeProperty('padding-right');
      if (el.style.getPropertyValue('margin-right')) el.style.removeProperty('margin-right');
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-scroll-locked') {
          cleanElement(document.body);
          cleanElement(document.documentElement);
        }
      }
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['data-scroll-locked'] });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-scroll-locked'],
    });

    (window as any).__COMMITMENT_FORM_WIDGET_SCROLL_LOCK_PREVENTED__ = true;
  }

  private injectFonts() {
    // Google Fonts (Pirata One + Montserrat fallback)
    const gfId = 'commitment-form-widget-fonts';
    if (!document.getElementById(gfId)) {
      const link = document.createElement('link');
      link.id = gfId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Pirata+One&family=Montserrat:wght@300;400;500;600;700;800;900&display=swap';
      document.head.appendChild(link);
    }
    // Adobe Fonts (Typekit) — Acme Gothic kit
    const akId = 'commitment-form-widget-typekit';
    if (!document.getElementById(akId)) {
      const link = document.createElement('link');
      link.id = akId;
      link.rel = 'stylesheet';
      link.href = 'https://use.typekit.net/asn7wkw.css';
      document.head.appendChild(link);
    }
  }

  private injectStyles() {
    if (!this.shadowRoot) return;

    const resetCSS = `
      *, *::before, *::after { box-sizing: border-box; }
      /* Host stays full-width and transparent — external CSS could override
         :host selectors, so we don't depend on them for layout. */
      :host {
        display: block;
        width: 100%;
        background: transparent;
      }
      /* The mount point fills the host container. Width constraint, if
         any, is the embedding page's responsibility — the widget renders
         as wide as its container provides. */
      #commitment-form-widget-app {
        width: 100%;
        margin-left: auto;
        margin-right: auto;
      }
      :host {
        --tw-border-spacing-x: 0; --tw-border-spacing-y: 0;
        --tw-translate-x: 0; --tw-translate-y: 0;
        --tw-rotate: 0; --tw-skew-x: 0; --tw-skew-y: 0;
        --tw-scale-x: 1; --tw-scale-y: 1;
        --tw-pan-x: ; --tw-pan-y: ; --tw-pinch-zoom: ;
        --tw-scroll-snap-strictness: proximity;
        --tw-gradient-from-position: ; --tw-gradient-via-position: ; --tw-gradient-to-position: ;
        --tw-ordinal: ; --tw-slashed-zero: ; --tw-numeric-figure: ;
        --tw-numeric-spacing: ; --tw-numeric-fraction: ;
        --tw-ring-inset: ; --tw-ring-offset-width: 0px;
        --tw-ring-offset-color: #fff; --tw-ring-color: rgb(59 130 246 / 0.5);
        --tw-ring-offset-shadow: 0 0 #0000; --tw-ring-shadow: 0 0 #0000;
        --tw-shadow: 0 0 #0000; --tw-shadow-colored: 0 0 #0000;
        --tw-inset-shadow: 0 0 #0000; --tw-inset-ring-shadow: 0 0 #0000;
        --tw-border-style: solid;
        --tw-blur: ; --tw-brightness: ; --tw-contrast: ; --tw-grayscale: ;
        --tw-hue-rotate: ; --tw-invert: ; --tw-saturate: ; --tw-sepia: ;
        --tw-drop-shadow: ; --tw-backdrop-blur: ; --tw-backdrop-brightness: ;
        --tw-backdrop-contrast: ; --tw-backdrop-grayscale: ; --tw-backdrop-hue-rotate: ;
        --tw-backdrop-invert: ; --tw-backdrop-opacity: ; --tw-backdrop-saturate: ;
        --tw-backdrop-sepia: ;
        --tw-contain-size: ; --tw-contain-layout: ; --tw-contain-paint: ; --tw-contain-style: ;
      }
    `;

    let shadowCSS = styleText
      .replace(/:root\b/g, ':host')
      .replace(/\bbody\b/g, '#commitment-form-widget-app')
      .replace(/(#commitment-form-widget-app\s*{[^}]*?)overflow-y:\s*scroll\s*!important;?/g, '$1');

    // Rewrite asset paths so they point at widgets.woodsidebible.org instead
    // of the host page's origin. ASSET_BASE_URL is baked in at build time;
    // when empty (next dev), /assets/... resolves against the dev server.
    //
    // Capture the optional opening quote and re-emit it on the replacement —
    // otherwise `url(/assets/X)` becomes `url('https://.../X)` with an
    // unterminated string, and the browser silently drops the whole rule.
    if (ASSET_BASE_URL) {
      shadowCSS = shadowCSS.replace(/url\((['"]?)\/assets\//g, `url($1${ASSET_BASE_URL}/assets/`);
    }

    const style = document.createElement('style');
    style.textContent = resetCSS + '\n' + shadowCSS;
    this.shadowRoot.appendChild(style);
  }

  destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

if (typeof window !== 'undefined') {
  /** Resolve config from (in order):
   *  1. window.COMMITMENT_FORM_WIDGET_CONFIG  — full programmatic config
   *  2. <div id="commitment-form-root" data-campaign-id="194">  — clean 2-line embed
   *
   *  The data-attribute path is what we recommend on host pages; the JS object
   *  is kept around for hosts that need to set apiBaseUrl/theme too.
   */
  const resolveConfig = (): WidgetConfig | null => {
    const programmatic = window.COMMITMENT_FORM_WIDGET_CONFIG;
    if (programmatic && programmatic.campaignId) return programmatic;

    const containerId = programmatic?.containerId || 'commitment-form-root';
    const el = document.getElementById(containerId);
    const attr = el?.getAttribute('data-campaign-id');
    const campaignId = attr ? parseInt(attr, 10) : NaN;
    if (Number.isFinite(campaignId) && campaignId > 0) {
      return { ...(programmatic || {}), containerId, campaignId };
    }
    return null;
  };

  const start = () => {
    const config = resolveConfig();
    if (!config) {
      console.error(
        'Commitment Form Widget: campaignId required. Add `data-campaign-id="..."` to the container ' +
          'or set `window.COMMITMENT_FORM_WIDGET_CONFIG = { campaignId: ... }` before this script.'
      );
      return;
    }
    const widget = new CommitmentFormWidgetInstance(config);
    widget.init();
    window.CommitmentFormWidget = widget;
  };

  // The re-entrancy guard MUST run synchronously inside the IIFE — vite's IIFE
  // wrapper is `var CommitmentFormWidget = (function () { ... })();`, so once
  // the IIFE returns, `window.CommitmentFormWidget` becomes the CLASS itself
  // (truthy). If we checked the guard inside start() and start() was deferred
  // via DOMContentLoaded, the guard would trip on the class and we'd never
  // init. Matches the pattern campus-picker and locations widgets use.
  if (!window.CommitmentFormWidget) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  }
}

export default CommitmentFormWidgetInstance;
